import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/app-shell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatCurrency } from "@/lib/format";
import { useActiveCompany } from "@/hooks/use-active-company";
import { ChevronDown, ChevronRight, ArrowUpDown } from "lucide-react";

export const Route = createFileRoute("/_authenticated/reports/margin")({
  head: () => ({
    meta: [
      { title: "Marginrapport og arbeidsliste | VERQO" },
      {
        name: "description",
        content:
          "Oversikt per varegruppe over antall varer uten fastsatt kundepris, snitt innkjøpsrabatt og snitt dekningsgrad.",
      },
      { property: "og:title", content: "Marginrapport og arbeidsliste | VERQO" },
      {
        property: "og:description",
        content: "Se hvilke varegrupper som mangler kundepris, og marginen per prosjekt.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: MarginReportPage,
});

type GroupStat = {
  code: string;
  name: string;
  count: number;
  avgDiscount: number | null;
  avgDg: number | null;
  missing: number;
};

type RpcGroupRow = {
  code: string;
  name: string;
  antall_varer: number;
  snitt_rabatt: number | null;
  snitt_dg: number | null;
  antall_uten_kundepris: number;
};

const pct = (n: number | null) =>
  n == null ? "—" : `${n.toFixed(1).replace(".", ",")} %`;

const toStat = (r: RpcGroupRow): GroupStat => ({
  code: r.code,
  // Navnene er lagret som "0501 Skurlast" – strip koden for tabellvisning.
  name: (r.name ?? "Uten varegruppe").replace(/^\d{2,4}\s+/, ""),
  count: Number(r.antall_varer ?? 0),
  avgDiscount: r.snitt_rabatt == null ? null : Number(r.snitt_rabatt),
  avgDg: r.snitt_dg == null ? null : Number(r.snitt_dg),
  missing: Number(r.antall_uten_kundepris ?? 0),
});

async function fetchGroups(moduleKey: string, parent: string | null): Promise<GroupStat[]> {
  const { data, error } = await supabase.rpc("margin_report_groups", {
    _module_key: moduleKey,
    _parent_code: parent ?? undefined,
  });
  if (error) throw error;
  return ((data ?? []) as unknown as RpcGroupRow[]).map(toStat);
}

/** Undergrupper for én hovedgruppe – hentes først når gruppa åpnes. */
function SubGroupRows({
  moduleKey,
  parent,
  sort,
}: {
  moduleKey: string;
  parent: string;
  sort: (arr: GroupStat[]) => GroupStat[];
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["margin-report-subgroups", moduleKey, parent],
    queryFn: () => fetchGroups(moduleKey, parent),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <tr className="border-b text-muted-foreground">
        <td />
        <td className="p-2 pl-6 text-xs" colSpan={6}>
          Henter undergrupper …
        </td>
      </tr>
    );
  }

  return (
    <>
      {sort(data ?? []).map((s) => (
        <tr key={s.code} className="border-b text-muted-foreground">
          <td />
          <td className="p-2 pl-6 font-mono">{s.code}</td>
          <td className="p-2">{s.name}</td>
          <td className="p-2 text-right">{s.count.toLocaleString("nb-NO")}</td>
          <td className="p-2 text-right">{pct(s.avgDiscount)}</td>
          <td className="p-2 text-right">{pct(s.avgDg)}</td>
          <td className="p-2 text-right">{s.missing.toLocaleString("nb-NO")}</td>
        </tr>
      ))}
    </>
  );
}

function MarginReportPage() {
  const { activeModuleKey: moduleKey, companyId } = useActiveCompany();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [sortKey, setSortKey] = useState<keyof GroupStat>("missing");
  const [sortDesc, setSortDesc] = useState(true);
  const [projSort, setProjSort] = useState<{ key: string; desc: boolean }>({ key: "dg", desc: false });

  const { data: totals } = useQuery({
    queryKey: ["margin-report-totals", moduleKey],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("margin_report_totals", { _module_key: moduleKey });
      if (error) throw error;
      const row = (data as unknown as { total: number; priced: number }[])?.[0];
      return { total: Number(row?.total ?? 0), priced: Number(row?.priced ?? 0) };
    },
  });

  const { data: mains = [], isLoading } = useQuery({
    queryKey: ["margin-report-maingroups", moduleKey],
    queryFn: () => fetchGroups(moduleKey, null),
    staleTime: 5 * 60 * 1000,
  });


  const { data: projects = [] } = useQuery({
    queryKey: ["margin-report-projects", companyId, moduleKey],
    enabled: !!companyId,
    queryFn: async () => {
      const [{ data: projs }, { data: lines }] = await Promise.all([
        supabase.from("projects").select("id,name,project_number").eq("company_id", companyId).eq("module_key", moduleKey),
        supabase
          .from("project_lines")
          .select("project_id,quantity,unit_cost,unit_price")
          .eq("company_id", companyId)
          .eq("module_key", moduleKey)
          .limit(10000),
      ]);
      const byId = new Map<string, { name: string; number: string | null; cost: number; price: number }>();
      for (const p of projs ?? []) byId.set(p.id, { name: p.name, number: p.project_number, cost: 0, price: 0 });
      for (const l of lines ?? []) {
        const p = byId.get(l.project_id);
        if (!p) continue;
        p.cost += Number(l.quantity ?? 0) * Number(l.unit_cost ?? 0);
        p.price += Number(l.quantity ?? 0) * Number(l.unit_price ?? 0);
      }
      return [...byId.entries()]
        .map(([id, p]) => ({
          id,
          name: p.name,
          number: p.number,
          cost: p.cost,
          price: p.price,
          db: p.price - p.cost,
          dg: p.price > 0 ? ((p.price - p.cost) / p.price) * 100 : null,
        }))
        .filter((p) => p.cost > 0 || p.price > 0);
    },
  });

  const total = totals?.total ?? 0;
  const priced = totals?.priced ?? 0;



  const sortStats = (arr: GroupStat[]) =>
    [...arr].sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (typeof va === "string" || typeof vb === "string")
        return sortDesc ? String(vb).localeCompare(String(va)) : String(va).localeCompare(String(vb));
      const na = va == null ? -Infinity : Number(va);
      const nb = vb == null ? -Infinity : Number(vb);
      return sortDesc ? nb - na : na - nb;
    });

  const sortedMains = sortStats(mains);
  const sortedProjects = useMemo(() => {
    const key = projSort.key as "name" | "cost" | "price" | "db" | "dg";
    return [...projects].sort((a, b) => {
      if (key === "name") return projSort.desc ? b.name.localeCompare(a.name) : a.name.localeCompare(b.name);
      const na = a[key] == null ? -Infinity : Number(a[key]);
      const nb = b[key] == null ? -Infinity : Number(b[key]);
      return projSort.desc ? nb - na : na - nb;
    });
  }, [projects, projSort]);

  const toggleSort = (k: keyof GroupStat) => {
    if (k === sortKey) setSortDesc((d) => !d);
    else {
      setSortKey(k);
      setSortDesc(k !== "code" && k !== "name");
    }
  };

  const Th = ({ k, label, align = "right" }: { k: keyof GroupStat; label: string; align?: "left" | "right" }) => (
    <th className={`p-2 font-medium ${align === "right" ? "text-right" : "text-left"}`}>
      <button onClick={() => toggleSort(k)} className="inline-flex items-center gap-1 hover:text-foreground">
        {label}
        <ArrowUpDown className={`w-3 h-3 ${sortKey === k ? "text-primary" : "opacity-40"}`} />
      </button>
    </th>
  );

  const PTh = ({ k, label, align = "right" }: { k: string; label: string; align?: "left" | "right" }) => (
    <th className={`p-2 font-medium ${align === "right" ? "text-right" : "text-left"}`}>
      <button
        onClick={() =>
          setProjSort((s) => (s.key === k ? { key: k, desc: !s.desc } : { key: k, desc: k !== "name" }))
        }
        className="inline-flex items-center gap-1 hover:text-foreground"
      >
        {label}
        <ArrowUpDown className={`w-3 h-3 ${projSort.key === k ? "text-primary" : "opacity-40"}`} />
      </button>
    </th>
  );

  const missingTotal = total - priced;
  const share = total ? (priced / total) * 100 : 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Marginrapport og arbeidsliste"
        subtitle="Hvilke varegrupper mangler kundepris, hva er snittrabatt og dekningsgrad – og hvordan ligger prosjektene an."
      />

      <Card className="p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="text-sm">
            <span className="text-2xl font-bold">{priced.toLocaleString("nb-NO")}</span>
            <span className="text-muted-foreground"> av {total.toLocaleString("nb-NO")} priset</span>
          </div>
          <div className="text-xs text-muted-foreground">
            {missingTotal.toLocaleString("nb-NO")} varer mangler kundepris · {pct(share)} ferdig
          </div>
        </div>
        <div className="mt-3 h-2 rounded-full bg-muted overflow-hidden">
          <div className="h-full bg-primary transition-all" style={{ width: `${share}%` }} />
        </div>
      </Card>

      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
          Per varegruppe
        </h2>
        <Card className="overflow-x-auto">
          {isLoading ? (
            <div className="p-6 text-sm text-muted-foreground">Henter materiell …</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b">
                <tr>
                  <th className="w-8" />
                  <Th k="code" label="Kode" align="left" />
                  <Th k="name" label="Navn" align="left" />
                  <Th k="count" label="Varer" />
                  <Th k="avgDiscount" label="Snitt innkj.rabatt" />
                  <Th k="avgDg" label="Snitt DG" />
                  <Th k="missing" label="Uten kundepris" />
                </tr>
              </thead>
              <tbody>
                {sortedMains.map((m) => {
                  const open = !!expanded[m.code];
                  return (
                    <Fragment key={m.code}>
                      <tr
                        className="border-b bg-muted/30 font-medium cursor-pointer hover:bg-accent/50"
                        onClick={() => setExpanded((e) => ({ ...e, [m.code]: !open }))}
                      >
                        <td className="p-2">
                          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </td>
                        <td className="p-2 font-mono">{m.code}</td>
                        <td className="p-2">{m.name}</td>
                        <td className="p-2 text-right">{m.count.toLocaleString("nb-NO")}</td>
                        <td className="p-2 text-right">{pct(m.avgDiscount)}</td>
                        <td className="p-2 text-right">{pct(m.avgDg)}</td>
                        <td className="p-2 text-right">
                          {m.missing > 0 ? (
                            <Badge variant={m.missing === m.count ? "destructive" : "secondary"}>
                              {m.missing.toLocaleString("nb-NO")}
                            </Badge>
                          ) : (
                            <span className="text-success">0</span>
                          )}
                        </td>
                      </tr>
                      {open && (
                        <SubGroupRows moduleKey={moduleKey} parent={m.code} sort={sortStats} />
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
          Per prosjekt
        </h2>
        <Card className="overflow-x-auto">
          {sortedProjects.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">Ingen prosjektlinjer å regne margin på ennå.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b">
                <tr>
                  <PTh k="name" label="Prosjekt" align="left" />
                  <PTh k="cost" label="Sum kost" />
                  <PTh k="price" label="Sum kundepris" />
                  <PTh k="db" label="DB" />
                  <PTh k="dg" label="DG" />
                </tr>
              </thead>
              <tbody>
                {sortedProjects.map((p) => (
                  <tr key={p.id} className="border-b">
                    <td className="p-2">
                      {p.number && <span className="font-mono text-xs text-muted-foreground mr-2">{p.number}</span>}
                      {p.name}
                    </td>
                    <td className="p-2 text-right">
                      {p.cost === 0 && p.price > 0 ? (
                        <span className="text-warning">Kost mangler</span>
                      ) : (
                        formatCurrency(p.cost)
                      )}
                    </td>
                    <td className="p-2 text-right">{formatCurrency(p.price)}</td>
                    <td className="p-2 text-right">
                      {p.cost === 0 && p.price > 0 ? "—" : formatCurrency(p.db)}
                    </td>
                    <td
                      className={`p-2 text-right ${
                        p.dg == null ? "" : p.dg < 0 ? "text-destructive" : p.dg < 15 ? "text-warning" : "text-success"
                      }`}
                    >
                      {p.cost === 0 && p.price > 0 ? "Kost mangler" : pct(p.dg)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}
